import React from 'react';
import dayjs from 'dayjs';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { SearchField } from './components/SearchField';
import { ResultListItem } from './components/ResultListItem';
import { AllEventTypes, EventData } from './HeroSection.types';
import { joyEventTypes } from './HeroSection.utils';
import ItemSkeleton from './components/ItemSkeleton/ItemSkeleton';
import { useHeroSectionController } from './HeroSection.controller';

import {
  StyledSection,
  SearchWrapper,
  StyledHeading,
  StyledText,
  ResponseWrapper,
  CountText,
  NoDataMsg,
  ResultList,
  SuggestionsContainer,
  NoDataDetailText,
  ShowAllButton,
} from './HeroSection.styles';
import { useTranslation } from 'react-i18next';

const SECTION_ID = 'FindAnEvent_Hero_Section';

interface HeroSectionProps {
  eventType: AllEventTypes;
  onChangeEventType: (eventType: AllEventTypes) => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ eventType, onChangeEventType }) => {
  const {
    error,
    loading,
    shouldShowAll,
    loadingMessage,
    showSuggestions,
    displayedResults,
    filteredSearchResult,
    handleSubmit,
    setShouldShowAll,
    handleChangeInput,
  } = useHeroSectionController(eventType);
  const { t } = useTranslation('findAnEvent');
  const heroSectionData = t('findACouple.heroSection');

  return (
    <StyledSection id={SECTION_ID} paddingX={6} paddingY={6}>
      <Flex justifyContent="center" paddingBottom={[3, 5]}>
        <SearchWrapper paddingTop={[6, 8]} paddingBottom={[7, 10]} paddingX={[0, 6]}>
          <StyledHeading color="mono1" variant="hed56" tagName="h1" marginBottom={3}>
            {heroSectionData.findAnEvent}
          </StyledHeading>
          <StyledText color="mono1" variant="navParent18" tagName="h2" marginBottom={6}>
            {heroSectionData.searchNote}
          </StyledText>
          <Box marginTop={[5, 7]} paddingX={[3, 0]}>
            <SearchField
              options={joyEventTypes}
              selectAllLabel={heroSectionData.allEvents}
              value={eventType}
              inputError={error}
              labelFunction={(value) =>
                value === 'babyRegistry' ? t('findACouple.heroSection.types.babyRegistry') : t('findACouple.heroSection.types.wedding')
              }
              onChangeInput={handleChangeInput}
              onChangeSelect={onChangeEventType}
              onSubmit={handleSubmit}
              placeholder={heroSectionData.searchPlaceholder}
            />
          </Box>
        </SearchWrapper>
      </Flex>
      {(loading || showSuggestions || filteredSearchResult) && (
        <ResponseWrapper>
          <ResultList paddingBottom={[5, 8]}>
            {(loading || filteredSearchResult) && loadingMessage && (
              <CountText tagName="p" variant="featureHed17" color="red5" error={showSuggestions}>
                {loadingMessage}
              </CountText>
            )}

            {showSuggestions && (
              <SuggestionsContainer>
                <NoDataMsg tagName="p" variant="featureHed17" color="mono3">
                  {heroSectionData.suggestions.tryUsing}
                </NoDataMsg>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="sparkle">
                    ✨
                  </span>{' '}
                  {heroSectionData.suggestions.bothNames}
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="ring">
                    ✨
                  </span>{' '}
                  {heroSectionData.suggestions.properNames}
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="flower">
                    ✨
                  </span>{' '}
                  {heroSectionData.suggestions.nicknames}
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="flower">
                    ✨
                  </span>{' '}
                  {heroSectionData.suggestions.eventHandle}
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  {heroSectionData.suggestions.note}
                </NoDataDetailText>
              </SuggestionsContainer>
            )}

            <Flex flexDirection="column">
              <ItemSkeleton ready={!loading}>
                {displayedResults &&
                  displayedResults.map((item: EventData, index) => {
                    const { ownerFullName, fianceeFullName, dateV0918, eventType } = item.event.info;
                    const { photo } = item.event;
                    const { disabled } = item.event.pageInfo;
                    let eventDate = '';
                    if (dateV0918 && dateV0918.timestamp) {
                      eventDate = dayjs(dateV0918.timestamp.substring(0, 10)).format('MMMM D, YYYY');
                    }
                    const website = item.event.website;

                    return (
                      <ResultListItem
                        key={`result-item-${index}`}
                        showBorderBottoom={index + 1 !== displayedResults.length}
                        ownerFullName={ownerFullName}
                        fianceeFullName={fianceeFullName}
                        eventDate={eventDate}
                        eventType={eventType}
                        website={website}
                        index={index}
                        photoUrl={photo && photo.url ? photo.url : null}
                        registryPageDisabled={disabled}
                      />
                    );
                  })}
              </ItemSkeleton>
            </Flex>
          </ResultList>
          {!shouldShowAll && !loading && filteredSearchResult && filteredSearchResult.length > 5 && (
            <ShowAllButton id="findResultShowAllBtn" onClick={(e) => setShouldShowAll(true)}>
              {heroSectionData.showAllCta}
            </ShowAllButton>
          )}
        </ResponseWrapper>
      )}
    </StyledSection>
  );
};
